import cloneDeep from 'lodash.clonedeep'
import zionDebug from '@fs/zion-debug'
import { getRawValue, replaceIndex } from '../../../shared/utils/utils'

const debug = zionDebug('rip:R2:reducer')

// Initial state for the reducer
export const defaultInitialState = {
  addedSexId: null,
  // Changes will handle knowing which fields need to be sent off to SLS and may help with UNDO/REDO in the future
  changes: [],
  highlights: [], // All highlights pulled from the sls data (we only want to pull this when changing images or after saving the data)
  editHighlights: [], // Temporary state of highlights during editing
  recordHighlights: [], // All record level highlights pulled from the sls data
  recordEditHighlights: [], // Temporary state of record level highlights during editing
  editDispatches: [], // Dispatches that are currently queued up to be saved. Useful when working with flows across multiple panels. Reset the array after saving these dispatches.
  referenceHighlights: [], // All highlights pulled from data on the reference image
  referenceEditHighlights: [], // Temporary state of reference viewer highlights during editing
  referenceRecordHighlights: [], // All record level highlights pulled from the sls data
  referenceRecordEditHighlights: [], // Temporary state of record level highlights during editing
  selectedPersonId: '', // The person that was selected in SelectPerson
  selectedRecordId: '', // The person that was selected in SelectPerson
  createdPersonId: '', // The person that was created
  createdPersonNoRecord: false, // The person that was created
  highlightedPersonId: '', // The user hovers over a highlight or person in select person
  highlightedRecordId: '', // The user hovers over a record level highlight or person in select person
  editingSharedFieldId: '', // This is the field id being edited by the EditSHaredField panels
  activeFieldId: '', // This is used when we want to get to a single, specific field (e.g. NAME_GN)
  activeElementId: '', // This is used when we want to get to a group of fields (e.g. events)
  imagePermission: '',
  shouldLoadIndexedData: false,
  highlightsFieldInfo: {},
  creatingRect: false,
  updateZoom: null,
  splitScreenImageGrid: true, // Whether the reference viewer is in grid mode
  loading: true,
  initialView: null, // The view that was initially loaded
  currentView: null, // The current active view
  availableViews: ['index', 'fullText', 'explore'],

  // The group of apids (apids are what we derrive thumbnails from),
  // may be a sub-divided parent group, in which case, we will have the
  // ability to show thumbnails from the entire parent group or one of the child groups
  // This state caches the request to get all child groups, so we don't have to make a larege request every time
  childGroups: [],

  loadingGroupData: true,
  groupData: {},

  customPropChanges: false,
  previousIndex: 0,
  referenceFocusedIndex: 0, // The image index focused in the reference viewer (split screen)
  splitScreen: false,

  resetState: false,
  dontResetState: false,

  tabIndex: 0, // which tab is open, image index or group data

  historyFormat: localStorage.getItem('rip_preferences_format') || 'group', // Preffered format in the history view
  selectedHighlights: { main: [], splitScreen: [] }, // Highlights that have multiple fields attached to them. This is for handling linked fields
  addPersonPanelArgs: {
    referrer: null, // The panel that opened the add person panel
    recordId: null, // The record id to add the person to
    metadata: {}, // Any extra metadata to pass to the add person panel
  },
  temporaryPersonsDataForRelationships: {}, // This is where we store data about newly added persons that haven't been saved to SLS yet
}

/**
 * Handles all the states
 * @param {Object} State - The current value of the state
 * @param {Action} Action - The value that will be used to determine the next state
 * @return {Object} - The new state
 */
export default function reducer(state, action) {
  debug('reducer called: ', state, action.type, action)
  switch (action.type) {
    case 'BATCH_DISPATCHES':
      return action.value.reduce(reducer, state)
    case 'SET_STATE': // generic set state action
      return { ...state, ...action.value }
    case 'SET_CHILD_GROUPS': {
      return {
        ...state,
        childGroups: action.value,
      }
    }
    case 'SET_IMAGE_APIDS': {
      return {
        ...state,
        imageApids: action.value,
      }
    }
    case 'SET_GROUP_DATA': {
      return {
        ...state,
        groupData: action.value,
      }
    }
    case 'SET_LOADING_GROUP_DATA': {
      return {
        ...state,
        loadingGroupData: action.value,
      }
    }
    case 'SET_USER_ID': {
      return { ...state, userId: action.value }
    }
    case 'SET_TAB_INDEX': {
      return { ...state, tabIndex: action.value }
    }
    case 'SET_IMAGE_PERMISSION': {
      return { ...state, imagePermission: action.value }
    }
    case 'SET_LOAD_INDEXED_DATA': {
      return { ...state, shouldLoadIndexedData: action.value }
    }
    case 'NAVIGATE_IMAGE_RESET': {
      // We don't want to reset everything, but almost
      return {
        ...state,
        loading: defaultInitialState.loading,
        mode: defaultInitialState.mode,
        selectedPersonId: defaultInitialState.selectedPersonId,
        activeFieldId: defaultInitialState.activeFieldId,
        creatingRect: defaultInitialState.creatingRect,
        highlights: defaultInitialState.highlights,
        editHighlights: defaultInitialState.editHighlights,
        resetState: true,
      }
    }
    case 'LOADING': {
      return {
        ...state,
        loading: action.value,
      }
    }
    case 'SET_SPLIT_SCREEN_IMAGE_GRID': {
      return {
        ...state,
        splitScreenImageGrid: action.value,
      }
    }
    case 'SET_CREATING_RECT': {
      return {
        ...state,
        creatingRect: action.value,
      }
    }
    case 'SET_REFERENCE_FOCUSED_INDEX': {
      return {
        ...state,
        referenceFocusedIndex: action.value,
      }
    }
    case 'SET_SPLIT_SCREEN': {
      return {
        ...state,
        splitScreen: action.value,
      }
    }
    case 'SET_SELECTED_PERSON_ID': {
      return {
        ...state,
        selectedPersonId: action.value,
      }
    }
    case 'SET_CREATED_PERSON_ID': {
      return {
        ...state,
        createdPersonId: action.value,
      }
    }
    case 'SET_CREATED_PERSON_NO_RECORD': {
      return {
        ...state,
        createdPersonNoRecord: action.value,
      }
    }
    case 'SET_HIGHLIGHTED_PERSON_ID': {
      return {
        ...state,
        highlightedPersonId: action.value,
      }
    }
    // Adds a custom property to any of our arrays in state
    // e.g. elementType or 'record' and property name of 'WasChanged'
    // makes the specific record now have a 'customPropertyWasChanged' key val pair
    case 'ADD_CUSTOM_PROPERTY': {
      // Property name should be Pascal case
      const { elementType, id, propertyKey, propertyValue, override = false } = action.value
      const elementArray = state[elementType]
      const index = elementArray.findIndex((ele) => ele.id === id)
      const ele = elementArray[index]
      if (index < 0) return state
      const existingProp = ele[`customProperty${propertyKey}`]
      let newElementArray
      // If we're adding an object to an object, don't overwrite other keys
      if (existingProp && typeof existingProp === 'object' && typeof propertyValue === 'object' && !override) {
        newElementArray = replaceIndex(elementArray, index, {
          ...ele,
          [`customProperty${propertyKey}`]: Object.assign(existingProp, propertyValue),
        })
        // If we're adding an array to an array, just push
      } else if (existingProp && Array.isArray(existingProp) && Array.isArray(propertyValue) && !override) {
        newElementArray = replaceIndex(elementArray, index, {
          ...ele,
          [`customProperty${propertyKey}`]: existingProp.concat(propertyValue),
        })
        // If any other case, just change to the new prop
      } else {
        newElementArray = replaceIndex(elementArray, index, {
          ...ele,
          [`customProperty${propertyKey}`]: propertyValue,
        })
      }
      return {
        ...state,
        [elementType]: newElementArray,
        customPropChanges: true,
      }
    }
    case 'CLEAR_CUSTOM_PROPS': {
      const filterCustomProperties = (type) => [
        ...state[type].map((r) => {
          Object.keys(r).forEach((key) => {
            if (key.startsWith('customProperty')) delete r[key]
          })
          return r
        }),
      ]
      if (!state.customPropChanges || !state.fields?.length) return state
      return {
        ...state,
        customPropChanges: false,
        records: filterCustomProperties('records'),
        fields: filterCustomProperties('fields'),
        relationships: filterCustomProperties('relationships'),
        highlights: filterCustomProperties('highlights'),
        persons: filterCustomProperties('persons'),
        events: filterCustomProperties('events'),
      }
    }
    case 'SET_ACTIVE_FIELD_ID': {
      return {
        ...state,
        activeFieldId: action.value,
      }
    }
    // This is the current ID of the element (group of fields) to focus on
    case 'SET_ACTIVE_ELEMENT_ID': {
      return { ...state, activeElementId: action.value }
    }
    case 'SET_EDITING_SHARED_FIELD_ID': {
      return {
        ...state,
        editingSharedFieldId: action.value,
      }
    }
    case 'SET_HIGHLIGHTS_FIELD_INFO': {
      return {
        ...state,
        highlightsFieldInfo: action.value,
      }
    }
    case 'SET_HIGHLIGHTS': {
      return {
        ...state,
        highlights: cloneDeep(action.value), // array of rects
      }
    }
    case 'SET_REFERENCE_HIGHLIGHTS': {
      return {
        ...state,
        referenceHighlights: cloneDeep(action.value), // array of rects
      }
    }
    case 'SET_REFERENCE_EDIT_HIGHLIGHTS': {
      return {
        ...state,
        referenceEditHighlights: cloneDeep(action.value), // array of rects
      }
    }
    // This is the previous image focused index
    case 'SET_PREVIOUS_FOCUSED_INDEX': {
      return { ...state, previousIndex: action.value }
    }
    case 'SET_EDIT_HIGHLIGHTS': {
      return {
        ...state,
        editHighlights: cloneDeep(action.value), // array of rects
      }
    }
    case 'SET_EDIT_DISPATCHES': {
      return {
        ...state,
        editDispatches: action.value,
      }
    }
    case 'REVERT_HIGHLIGHTS': {
      return {
        ...state,
        editHighlights: cloneDeep(state.highlights), // array of rects
        referenceEditHighlights: cloneDeep(state.referenceHighlights),
      }
    }

    // New cache navigate dispatches
    case 'NAV_SELECT_TO_VIEW': {
      const { personId, recordId } = action.value
      return {
        ...state,
        selectedPersonId: personId,
        selectedRecordId: recordId,
        mode: 'viewIndex',
      }
    }
    case 'NAV_VIEW_TO_EDIT': {
      const { personId, activeFieldId } = action.value

      return {
        ...state,
        selectedPersonId: personId,
        activeFieldId,
        changes: [],
        mode: 'editIndex',
      }
    }
    case 'DISABLE_AUTOSCROLL': {
      return {
        ...state,
        autoscroll: false,
      }
    }
    case 'ENABLE_AUTOSCROLL': {
      return {
        ...state,
        autoscroll: true,
      }
    }
    case 'NAV_VIEW_TO_SELECT': {
      return {
        ...state,
        mode: 'selectPerson',
        updateZoom: { panX: 0.5, panY: 0.5, zoom: 1 },
      }
    }
    case 'NAV_EDIT_TO_VIEW': {
      const { personId, savedState } = action.value
      return {
        ...savedState,
        mode: 'viewIndex',
        selectedPersonId: personId,
        changes: [],
      }
    }
    case 'NAV_EDIT_TO_EDIT': {
      const { personId } = action.value
      return {
        ...state,
        selectedPersonId: personId,
        changes: [],
      }
    }
    case 'NAV_ADD_TO_VIEW': {
      const { personId } = action.value

      return {
        ...state,
        selectedPersonId: personId,
        mode: 'viewIndex',
      }
    }
    case 'NAV_SELECT_TO_EDIT': {
      const { personId } = action.value
      return {
        ...state,
        mode: 'editIndex',
        selectedPersonId: personId,
      }
    }
    case 'SET_HISTORY_FORMAT': {
      localStorage.setItem('rip_preferences_format', action.value)

      return { ...state, historyFormat: action.value }
    }
    case 'SET_SELECTED_HIGHLIGHTS': {
      return { ...state, selectedHighlights: action.value }
    }
    case 'OPEN_SPLIT_SCREEN_TO_PAGE': {
      return { ...state, splitScreen: true, referenceFocusedIndex: action.value - 1, splitScreenImageGrid: false }
    }
    case 'SET_INITIAL_VIEW':
      return {
        ...state,
        initialView: action.value,
      }
    case 'SET_CURRENT_VIEW':
      return {
        ...state,
        currentView: action.value,
      }
    case 'SET_ADD_PERSON_PANEL_ARGS':
      return {
        ...state,
        addPersonPanelArgs: action.value,
      }
    case 'SET_TEMPORARY_PERSONS_DATA_FOR_RELATIONSHIPS':
      return {
        ...state,
        temporaryPersonsDataForRelationships: {
          ...state.temporaryPersonsDataForRelationships,
          [action.value.id]: action.value.data,
        },
      }
    case 'CLEAR_TEMPORARY_PERSONS_DATA_FOR_RELATIONSHIPS':
      return {
        ...state,
        temporaryPersonsDataForRelationships: {},
      }
    default: {
      throw new Error(`Unhandled type in R2 reducer: ${action.type}`)
    }
  }
}

/**
 * Change the zoom to match where the rectangle is
 * @param {Number} minX - the minimum x value
 * @param {Number} maxX - the maximum x value
 * @param {Number} minY - the minimum y value
 * @param {Number} maxY - the maximum y value
 * @returns {undefined}
 */
export function getZoom(minX, maxX, minY, maxY) {
  const panX = (minX + maxX) / 2
  const panY = (minY + maxY + 0.1) / 2
  const maxSize = Math.max(maxX, maxY)
  let zoom = 4 // average size of highlight or group of highlights
  if (maxSize < 0.3) zoom = 3 // minimum size of highlight or group of highlights
  if (maxSize > 0.6) zoom = 2 // medium to large highlight or group of highlights
  if (maxSize > 0.9 || maxSize === 0) zoom = 1 // very large highlight or group of highlights or no highlights
  return { panX, panY, zoom }
}

export function isBadChange(action, previousChangeForId) {
  const change = action.value?.change

  const isAFieldOrRelationshipChange = ['CHANGE_FIELD', 'CHANGE_RELATIONSHIP'].includes(action.value.type)
  const noOriginalValue = !change.originalValue
  const noValue = !getRawValue(change)
  const isSameAsLastValue = previousChangeForId && getRawValue(previousChangeForId) === getRawValue(change)

  return (
    isAFieldOrRelationshipChange &&
    // By checking if there's no original value and nothing as a value (for new fields)
    ((noOriginalValue && noValue) ||
      // By checking if it's the same as the last value so no duplicate changes occur
      isSameAsLastValue)
  )
}

export function getNewPersonObject(id, primary = false) {
  return {
    id,
    fieldIds: [],
    newPerson: true,
    nameIds: [
      {
        NAME: undefined,
        NAME_GIVEN: undefined,
        NAME_SURNAME: undefined,
      },
    ],
    primary,
  }
}

export function clearNewPersonFlag(persons, id) {
  const index = persons.findIndex((p) => p.id === id)
  const person = persons[index]
  return replaceIndex(persons, index, { ...person, newPerson: false })
}

export function updateRelationshipOtherPerson(state, relationshipId, selectedPersonId, newOtherPersonId) {
  const index = state.relationships.findIndex((r) => r.id === relationshipId)
  // const otherIndex = state.relationships.findIndex((f) => f.id === newOtherPersonId)
  const rel = state.relationships[index]
  const otherPersonKey = selectedPersonId === rel.person1Id ? 'person2Id' : 'person1Id'
  // const otherRel = state.relationships[otherIndex]
  const relationships = replaceIndex(state.relationships, index, {
    ...rel,
    [otherPersonKey]: newOtherPersonId,
  })
  return relationships
}

export function setPersonPrimary(persons, id, primary = true) {
  const personIndex = persons.findIndex((p) => p.id === id)
  // const recordIndex = state.records.findIndex((rec) => rec.personIds.includes(id))
  const person = persons[personIndex]
  // const record = state.records[recordIndex]

  // Do we have to do this every time? I feel like this should belong somewhere else
  // We could just have this change the person property (not in the record)
  // const newPersonIds = [id, ...record.personIds.filter((pers) => pers !== id)]

  return replaceIndex(persons, personIndex, { ...person, primary })
  // const records = replaceIndex(state.records, recordIndex, { ...record, newPersonIds })
}
